function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { DialogOverlay } from '@reach/dialog';
import Button from '../buttons/button';
import Card from '../cards/card';
import { size4, size8, size12, size14, size16, size18, size24, size32, size48 } from '../theme/scale';
import { breakpoint } from '../theme/breakpoints';
import { z10, z50 } from '../theme/z-index';
import { Heading } from '../typography';
import '@reach/dialog/styles.css';
const Actions = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-start;
  margin-top: ${size24};
  z-index: ${z10};
  ${({
  fullWidth
}) => fullWidth && `
    > * {
      flex: 1
    }
  `}
`;
const ConfirmButton = styled(Button)`
  margin-left: ${size8};
`;
const StyledHeading = styled(Heading)`
  font-size: ${p => p.size};
  ${p => p.bottomSpace ? `margin-bottom: ${p.bottomSpace}` : ''};
`;
const StaticHeaderWrapper = styled.div`
  margin-top: -${size32};
  padding-bottom: ${size14};
`;
const ScrollableWrapper = styled.div`
  ${({
  height
}) => height && css`
      max-height: ${height};
      overflow-y: auto;
      padding-bottom: ${size48};
    `};
`;
const Footer = styled.div`
  margin-top: ${size12};

  ${breakpoint.md`
    margin-top: ${size16};
  `}
`;
const MaxHeightActions = styled(Actions)`
  position: absolute;
  right: 0;
  padding-right: ${size24};
  width: 100%;
  bottom: ${size16};
  background: rgba(255, 255, 255, 0.7);
`;
const StyledDialogOverlay = styled(DialogOverlay)`
  z-index: ${z50};
  backdrop-filter: blur(2px);
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;

  // @hack Chromium browsers (at least around Chromium v83.0) started exhibiting
  // weird jumpy behavior when 2 dialogs are opened at the same time.
  // Promoting this element to a separate layer prevents that.
  transform: scale3d(1, 1, 1);

  // Position the DialogContent in the middle of the screen
  display: flex;
  justify-content: center;
  align-items: center;

  [data-reach-dialog-content] {
    touch-action: manipulation;
    position: relative;
    background: transparent;
    box-shadow: none;
    width: auto;
  }
`;
const StaticHeader = ({
  title,
  subtitle
}) => {
  return /*#__PURE__*/React.createElement(StaticHeaderWrapper, null, /*#__PURE__*/React.createElement(StyledHeading, {
    bottomSpace: size4,
    weight: "regular",
    size: size24,
    color: "gray900",
    id: "dialog-heading"
  }, title), /*#__PURE__*/React.createElement(StyledHeading, {
    size: size18,
    color: "gray700"
  }, subtitle));
};
export const Dialog = ({
  title,
  children,
  onDismiss,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  cancelVariant = 'tertiary',
  disableConfirm = false,
  width,
  maxWidth,
  dismissOnEsc = false,
  dismissOnClickAway = false,
  fullWidthButtons = false,
  hideCloseButton = false,
  actionIconSize = 16,
  actionIconColor,
  maxHeight,
  subtitle,
  ariaLabelledBy,
  headerIconProps,
  wrapText,
  footer,
  ...props
}) => {
  const node = useRef(null);
  const hasActions = useMemo(() => onConfirm != null || onCancel != null, [onConfirm, onCancel]);
  const ActionsComponent = useMemo(() => maxHeight ? MaxHeightActions : Actions, [maxHeight]);
  const hasGradientFooter = useMemo(() => hasActions && maxHeight, [hasActions, maxHeight]);
  const cardProps = {
    width,
    wrapText,
    maxWidth,
    className: props.className,
    title: title && subtitle ? undefined : title,
    actionIconSize,
    iconColor: actionIconColor,
    hideCloseButton,
    titleIcon: headerIconProps?.type,
    titleIconColor: headerIconProps?.color,
    titleIconSize: headerIconProps?.size,
    outlineIcons: headerIconProps?.outlined
  };
  const onDismissDialog = useMemo(() => {
    // Reach Dialog handles dismissing on Escape/ClickAway.
    // If we want to disable it we need to pass undefined for onDismiss.
    if (dismissOnEsc || dismissOnClickAway) {
      return onDismiss;
    }
  }, [dismissOnEsc, dismissOnClickAway, onDismiss]);
  return /*#__PURE__*/React.createElement(StyledDialogOverlay, _extends({}, props, {
    onDismiss: onDismissDialog,
    initialFocusRef: node
  }), /*#__PURE__*/React.createElement(Card, _extends({
    ref: node,
    actionIcon: "X",
    actionHandler: onDismiss,
    elevation: "6",
    underlineHeader: false,
    isModal: true,
    hasGradientFooter: !!hasGradientFooter,
    ariaLabelledBy: ariaLabelledBy || 'dialog-heading'
  }, cardProps), subtitle && title && /*#__PURE__*/React.createElement(StaticHeader, {
    subtitle,
    title
  }), /*#__PURE__*/React.createElement(ScrollableWrapper, {
    id: "body-content",
    height: maxHeight,
    tabIndex: maxHeight ? 0 : -1
  }, children), hasActions && /*#__PURE__*/React.createElement(ActionsComponent, {
    id: "actions-component",
    fullWidth: fullWidthButtons
  }, onConfirm && /*#__PURE__*/React.createElement(ConfirmButton, {
    variant: confirmVariant,
    disabled: disableConfirm,
    onClick: onConfirm
  }, confirmText), onCancel && /*#__PURE__*/React.createElement(Button, {
    variant: cancelVariant,
    onClick: onCancel
  }, cancelText)), footer && /*#__PURE__*/React.createElement(Footer, {
    id: "dialog-footer"
  }, footer)));
};
export default Dialog;
//# sourceMappingURL=index.js.map